// ---------------------------------------------------------------------------
// Feature-Flag-Registry ("Feature-Flag-Buch")
// ---------------------------------------------------------------------------
// Single source of truth for UI features that are intentionally hidden behind a
// flag — work-in-progress, pending data validation, or a staged rollout. Every
// entry is documented so it is always clear WHAT is gated, WHY it is hidden,
// SINCE when and who owns the decision. The human-readable overview lives in
// FEATURE_FLAGS.md at the repository root; keep both in sync.
//
// Lifecycle: when a feature is permanently released, flip nothing — instead
// remove its entry here, delete the gate at the call site, and strike the row
// in FEATURE_FLAGS.md (move it to the "Retired" section with the release date).
//
// Every entry carries a `stage` — the ONE place a feature's state is decided:
//
//   'live'      On everywhere. Its changelog items may sit in a published release.
//   'upcoming'  Hard off on master/live, on in staging. The feature is announced:
//               its items stay in the in-development changelog entry and may
//               tease staff under "Demnächst verfügbar".
//   'secret'    Hard off on master/live, on in staging, AND not announced at all.
//               While the flag is off, nothing about it may appear anywhere a
//               live user can read — no changelog item, no teaser, no guide, no
//               title. Currently: the AI phone assistant and its evaluation.
//   'stagingOnly' Hard off on master/live, on in staging, and not announced either.
//               The state for work that is simply NOT FINISHED: the code is
//               merged to master so both branches stay in step, and the switch
//               keeps it from taking effect in production. Unlike 'secret' it
//               carries no product decision — nobody chose to keep it quiet, it
//               is just not done — and it needs no changelog item at all until
//               the work is ready. This is what makes "only on staging" cheaper
//               than not merging, which is how the branches drifted apart.
//   'postponed' Hard off EVERYWHERE, staging included, and not announced either.
//               The project behind it is on hold ("verschoben"): the code stays
//               in the repository, but there is nothing to review, so it comes
//               off the test environment as well. Currently: the chat agent.
//
// 'upcoming' and 'secret' are fully usable on staging — between those two the
// difference is only what may be LEARNED about them on live, never what the code
// does. 'postponed' is the one state that also changes what staging shows. Keep
// `stage` in sync with the same registry in funzone-management-frontend-new,
// with FEATURE_FLAG_STAGES in the backend's util/changelog-feature-flags.js,
// and with FEATURE_FLAGS.md. All four together or none.
//
// Resolution order for a flag:
//   1. An explicit Vite env var `VITE_FEATURE_<UPPER_SNAKE_KEY>` set to the
//      string 'true' or 'false' always wins (e.g. VITE_FEATURE_QR_TICKETS).
//   2. A postponed flag is OFF — staging does not bring it back, because there
//      is nothing to review while its project is on hold.
//   3. On STAGING every other registered flag is ON, so anything gated can be
//      reviewed there without ever being exposed on live.
//   4. Otherwise `stage === 'live'` decides — which is how a flag stays hidden
//      on live/master.
// Staging is recognised by VITE_IS_STAGING=true (set in .env.staging, used by
// `npm run build:staging`).
//
// Where a flag becomes visible is part of its entry, so the UI can point the
// notice at the page it belongs to instead of showing one permanent bar:
//   • `pageRoutes`    — routes that exist or render ONLY because the flag is on.
//                       Whoever sees them is looking at something that is not
//                       there on live.
//   • `partialRoutes` — pages that exist on live too, but carry a gated part
//                       (a section, a tab, an extra action).
// Sub-paths are covered automatically ('/airkey' also matches
// '/airkey/mappings'). Surfaces without a route of their own — a button inside
// the booking-details modal, a global keyboard/scanner listener — stay empty on
// purpose: they are reachable from nearly every view, and listing them per route
// would put the bar back on every page. They remain documented in `note` and in
// the environment chip's tooltip.

export const FEATURE_FLAGS = {
  callProtocol: {
    label: 'Gesprächsprotokolle',
    description:
      'Protokoll zu jedem eingehenden Anruf — öffnet sich automatisch, sobald das Telefon beim Mitarbeiter klingelt, und ist bei einer Übergabe durch den KI-Agenten vorbefüllt.',
    stage: 'upcoming',
    since: '2026-08-10',
    owner: 'max@laserzone.de',
    // The list view only exists behind the flag; the call-center statistic keeps
    // its other sections on live and only adds the protocol section here. The
    // dialog that opens on a ringing phone has no route of its own.
    pageRoutes: ['/callProtocols'],
    partialRoutes: ['/callCenterStats'],
    note: 'Vorerst nur auf Staging, bis der Ablauf mit dem Call Center abgestimmt ist. Das Backend blockt zusätzlich alle /api/v1/callProtocol/*-Endpoints mit 503 CALL_PROTOCOL_DISABLED und legt beim Comdesk-Event ringing_outbound kein Protokoll an, solange CALL_PROTOCOL_ENABLED dort nicht greift — beide Seiten müssen zum Aktivieren gesetzt sein. Rechte: callProtocol:read (eigene), callProtocol:read:all (alle Mitarbeiter), callProtocol:write.',
  },
  pagerBriefing: {
    label: 'Pager-Ruf zum Briefing',
    description:
      'LRS-Coaster-Pager für Gäste — Zuweisung beim Check-in über einen Kartenauswahl-Dialog in der Briefing-Ansicht und der Ruf "Rufen", der den Coaster der Gruppe funken lässt.',
    stage: 'secret',
    since: '2026-09-04',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: [],
    note: 'Ohne Oberfläche in diesem Dashboard — das Feature ist nach dem Feature-Freeze nur im führenden funzone-management-frontend-new gebaut. Der Eintrag steht hier, weil der Zustand eines Flags an allen vier Stellen zusammen gepflegt wird. Geheim: darf live in keinem Changelog, Guide oder Teaser auftauchen. Das Backend gated die gesamte /api/v1/pager-API mit PAGER_BRIEFING_ENABLED (503 PAGER_BRIEFING_DISABLED).',
  },
  zoniHotline: {
    label: 'Zonja-Hotline',
    description:
      'Telefonnummer, unter der Mitarbeiter Zonja erreichen: First-Level-Support, Meldungen und alles, was Zonja im Chat kann. Anmeldung über eine Zahl, die im Management-Dashboard zum Antippen erscheint, oder über eine persönliche Service-PIN.',
    // Live (announced) since 11.10.2026 with Zonja's internal beta. The entry stays because the
    // backend switch ZONI_HOTLINE_ENABLED stays.
    stage: 'live',
    since: '2026-10-09',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: [],
    note: 'Ohne Oberfläche in diesem Dashboard - gebaut nur im führenden funzone-management-frontend-new (Bestätigungs-Pop-up, Service-PIN in den Einstellungen). Der Eintrag steht hier, weil der Zustand eines Flags an allen vier Stellen zusammen gepflegt wird. Live seit dem 11.10.2026 (vom 09.10. bis 11.10. geheim, aber live): angekündigt mit der internen Beta von Zonja. Der einzige Schalter bleibt ZONI_HOTLINE_ENABLED im Backend; deshalb bleibt auch dieser Eintrag.',
  },
  voiceAi: {
    label: 'KI-Telefonie',
    description:
      'Auswertung des KI-Agenten am Telefon — Abschnitt "KI-Telefonie" in der Call-Center-Statistik (KPI-Karten, Tagesverlauf, Volumenaufteilung, Gesprächsausgänge, Themen).',
    stage: 'secret',
    since: '2026-08-09',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: ['/callCenterStats'],
    note: 'Vorerst nur auf Staging, bis der Post-Call-Sync gegen die echte ElevenLabs-API verifiziert ist — das Feld-Mapping konnte nicht gegen einen echten Response geprüft werden. Das Backend liefert den `ai`-Block nur bei gesetztem VOICE_AI_ENABLED und lässt den Sync-Cron sonst aus. Achtung: Das Flag betrifft ausschliesslich die Auswertung — die Voice-Tool-Endpunkte (/api/v1/agent/voice/*, MCP) laufen weiter, der produktive KI-Agent wird davon nicht abgeschaltet.',
  },
  aiControl: {
    label: 'KI-Steuerung',
    description:
      'Zentrale KI-Steuerung: versionierte KI-Prompts mit ElevenLabs-Push, KI-Entwicklungsbedarf, KI-Statistik, die Prüf-Leiste im Agenten-Chat-Audit samt nächtlichem Lernlauf und der KI-Rohtext der Wissensdatenbank. Ohne Oberfläche in diesem Dashboard.',
    stage: 'secretLive',
    since: '2026-09-08',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: [],
    note: 'Ohne Oberfläche in diesem Dashboard — das Feature ist nach dem Feature-Freeze nur im führenden funzone-management-frontend-new gebaut (/ai-prompts, /aiStatistics, Prüf-Leiste im Agenten-Chat-Audit, KI-Rohtext in /knowledge). Der Eintrag steht hier, weil der Zustand eines Flags an allen vier Stellen zusammen gepflegt wird. Geheim, aber live seit dem 30.09.2026: an in jeder Umgebung, der nächtliche Lernlauf eingeschlossen, aber nur mit dem Vorschau-Recht preview:knowledge sichtbar (Rollen LeadEngineer und superAdmin); das Backend antwortet allen anderen auf /api/v1/aiPrompt, /api/v1/aiDevFinding und /api/v1/statistic/ai mit 404. Darf live in keinem Changelog, Guide oder Teaser auftauchen.',
  },
  agentChat: {
    label: 'KI-Chat („FunZone Assistent")',
    description:
      'Chat-Agent der Websites und des Buchungswidgets — hier der Menüeintrag "Eskalierte Chats" samt Route /escalated-chats.',
    stage: 'secret',
    since: '2026-08-09',
    owner: 'max@laserzone.de',
    pageRoutes: ['/escalated-chats'],
    partialRoutes: [],
    note: 'Seit dem 06.10.2026 wieder aufgenommen, deshalb "secret" statt "postponed" — live aus, auf Staging an. Gebaut wird er nur noch im führenden Dashboard ("Kundenchats" und "Chat-Test", dort am Vorschau-Recht preview:aiAgents statt an diesem Flag); hier bleibt "Eskalierte Chats" nur lauffähig und braucht dafür zusätzlich preview:aiAgents, weil das Backend die Eskalations-Endpunkte allen anderen mit 404 beantwortet. Ob der Agent läuft, entscheidet das Backend über AGENT_CHAT_ENABLED (aus heißt 503 AGENT_CHAT_DISABLED). Darf live in keinem Changelog, Guide oder Teaser auftauchen. Nicht betroffen: Mitarbeiter-Chat, Agenten-Chat-Audit und "Gemeldete Konversationen", der Mail-Agent.',
  },
  customerAccount: {
    label: 'Kundenkonto',
    description:
      'Kundenkonto im Customer-Frontend — Anmeldung per Magic-Link und persönlichem Zugangslink, Buchungs- und Gutscheinübersicht samt Gutschein-PDF, Einstellungen und Kontolöschung. Ohne Oberfläche in diesem Dashboard.',
    stage: 'secretLive',
    since: '2026-08-28',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: [],
    note:
      'Läuft in der Produktion und wird trotzdem nirgends angekündigt. Das Konto stand vom 30.08. bis 05.09.2026 zusammen mit dem Treueprogramm an diesem Schalter und ist genau daran gescheitert: Ein voller Erlebnis-Pass zahlte selbsttätig aus und schickte echten Kunden eine Gutscheinmail für ein Programm, das nie gestartet wurde. "Geheim, aber live" regelt nur, was über ein Feature gelesen werden darf, nicht was es von allein tut — deshalb hängt alles, was etwas herausgibt, seit dem 5. September am eigenen Flag loyaltyProgramme. Was hier bleibt, liest nur. Benutzbar ist das Konto erst nach einer ausdrücklichen Einwilligung; ohne sie antwortet jede Route außer /me, /me/consent, DELETE /me und /me/logout mit 403 CUSTOMER_CONSENT_REQUIRED. Schalter: CUSTOMER_ACCOUNT_ENABLED im Backend, VITE_CUSTOMER_ACCOUNT_ENABLED im Customer-Frontend, beide standardmäßig an.',
  },
  roleHandbook: {
    label: 'Rollen-Handbuch',
    description:
      'Ansicht „Mein Handbuch" im führenden Dashboard (funzone-management-frontend-new, Route /handbook): alle Abläufe, die die eigenen Rollen bedienen können, samt PDF-Download mit handbook:manage. Ohne Oberfläche in diesem Dashboard — es ist eingefroren.',
    stage: 'live',
    since: '2026-10-06',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: [],
    note: 'Seit 06.10.2026 live im führenden Dashboard. Hier nur registriert, damit die vier Stellen des Registers übereinstimmen. Backend-Schalter ROLE_HANDBOOK_ENABLED (503 ROLE_HANDBOOK_DISABLED).',
  },
  loyaltyProgramme: {
    label: 'Treueprogramm (Stempelkarte, Erlebnis-Pässe, Glücksrad)',
    description:
      'Alles am Kundenkonto, was von sich aus etwas herausgibt: Stempelkarte über alle Erlebniswelten, Erlebnis-Pässe je Marke und Glücksrad, dazu die drei Belohnungsmails. Im Dashboard hängt die Konfigurationsansicht "Treueprogramm" (Route /loyaltyProgram, Recht loyalty:manage) daran.',
    stage: 'postponed',
    since: '2026-09-05',
    owner: 'max@laserzone.de',
    pageRoutes: ['/loyaltyProgram'],
    partialRoutes: [],
    note:
      'Verschoben statt geheim: Das Programm ist auch auf Staging aus, weil es nichts zu begutachten gibt — es wird um eine ausdrückliche Einwilligung herum neu aufgesetzt und fängt dann bei null an (Startup-Migration 2026-09-05-loyalty-reset-to-zero; bereits ausgestellte Gutscheine bleiben unberührt und gültig). Anlass ist der Vorfall vom 30.08.-05.09.2026: 233 Freiplatz-Gutscheine an 205 echte Kunden aus einem Programm, das nie freigegeben wurde. Der Stempellauf im Cron und die Prüfung nach jeder Buchung sind entfernt, nicht nur abgeschaltet. Schalter: LOYALTY_PROGRAMME_ENABLED im Backend, VITE_FEATURE_LOYALTY_PROGRAMME hier.',
  },
  offlineFeeExperiments: {
    label: 'Preis-A/B-Tests Servicepauschale',
    description:
      'Zwei Preis-Experimente auf die Servicepauschale Vor-Ort-Zahlung (offline_fee_intro, offline_fee_increase) — Oberfläche nur im führenden Dashboard unter /experiments.',
    stage: 'secretLive',
    since: '2026-09-11',
    owner: 'max@laserzone.de',
    pageRoutes: [],
    partialRoutes: [],
    note: 'Geheim, aber live: Der Code läuft überall, damit ein Test überhaupt gestartet werden kann — angekündigt wird er nirgends, weder im Changelog noch in einem Guide oder Teaser, auch nicht als Nebensatz in einem fremden Item. Ein Kunde darf nicht herausfinden können, dass die ihm berechnete Servicepauschale ein Testarm ist. Tragfähig ist dieser Zustand nur wegen des zweiten Schalters: Was einen Kunden erreicht, hängt nicht an diesem Flag, sondern daran, dass ein Mensch einen Test im Dashboard einschaltet — beide Experimente liegen mit defaultEnabled: false im Katalog. Dieses Flag ist die Notbremse darüber (Backend: OFFLINE_FEE_EXPERIMENTS_ENABLED). Dieses Dashboard hat keine Oberfläche dafür; der Eintrag steht hier, weil der Zustand an allen vier Stellen zusammen gepflegt wird.',
  },
  competitorStatistics: {
    label: 'Konkurrenten-Statistik',
    description:
      'Auswertung der Verfügbarkeiten von Wettbewerbern — Menüeintrag "Konkurrenten-Statistik" und Route /competitors, Recht competitorAvailability:read.',
    stage: 'secretLive',
    since: '2026-09-02',
    owner: 'max@laserzone.de',
    pageRoutes: ['/competitors'],
    partialRoutes: [],
    note: 'Läuft in der Produktion und bleibt bewusst unerwähnt: Die Ansicht ist nicht für jeden Mitarbeiter gedacht, deshalb darf nichts darüber in einem Changelog, einem Guide oder einem Teaser stehen — auch nicht als Nebensatz in einem fremden Item. Wer sie sehen darf, entscheidet weiterhin allein das Recht competitorAvailability:read; das Flag schaltet die Ansicht nicht ab, es hält sie nur aus allem heraus, was live lesbar ist.',
  },
  staffCostAreaOverview: {
    label: 'Personalkosten (Gesamt / Bereiche)',
    description:
      'Unternehmensweite Personalkosten-Statistik inkl. aller Verwaltungs-/Office-Bereiche — Menüeintrag "Personalkosten (Gesamt / Bereiche)" und Route /areaStaffCostStatistics.',
    stage: 'upcoming',
    since: '2026-07-27',
    owner: 'max@laserzone.de',
    // Only the standalone overview is gated; the area-locked embedding inside the
    // IT statistic stays visible on live and therefore gets no notice.
    pageRoutes: ['/areaStaffCostStatistics'],
    partialRoutes: [],
    note: 'Vorerst versteckt, bis Bereichs-/Rollenzuordnung und hinterlegte Gehälter vollständig verifiziert sind. Betrifft nur die Gesamt-/Bereichsübersicht; die IT-Personalkosten (nur IT-Bereich) in der IT-Statistik bleiben sichtbar. Der Backend-Endpoint bleibt rechtebasiert geschützt und wird durch das Flag nicht verändert.',
  },
  qrTickets: {
    label: 'QR-Code-Tickets',
    description:
      'PDF-Eintrittstickets mit QR-Code — Button "Ticket herunterladen" in den Buchungsdetails und der globale Handscanner-Listener, der aus jeder Ansicht heraus einen gescannten Ticket-Code annimmt und eincheckt.',
    stage: 'upcoming',
    since: '2026-08-08',
    owner: 'max@laserzone.de',
    // Booking-details button plus a listener that is active in every view — no
    // route of its own, so no page banner.
    pageRoutes: [],
    partialRoutes: [],
    note: 'Vorerst versteckt, bis der Einlass-Prozess mit den Standorten abgestimmt ist. Das Backend blockt zusätzlich alle /api/v1/ticket/*-Endpoints, solange dort TICKETS_ENABLED=true fehlt, und hängt kein Ticket an Kundenmails — beide Seiten müssen zum Aktivieren gesetzt werden. Rechte (ticket:read, ticket:scan) und bereits vergebene Ticket-Codes bleiben erhalten.',
  },
  b2bStatistics: {
    label: 'B2B-Statistik',
    description:
      'Einbuchungen des B2B-Teams gegen dessen Personalkosten — Menüeintrag "B2B-Statistik" und Route /b2bStatistics.',
    stage: 'upcoming',
    since: '2026-08-08',
    owner: 'max@laserzone.de',
    pageRoutes: ['/b2bStatistics'],
    partialRoutes: [],
    note: 'Vorerst nur auf Staging, aus denselben Gründen wie die Personalkosten-Gesamtübersicht: Die Zahlen hängen an der Rollen-/Bereichszuordnung und den hinterlegten Stundenlöhnen und sind noch nicht verifiziert. Der Backend-Endpoint bleibt rechtebasiert geschützt (statistic:read / :staffCost / :b2b) und wird durch das Flag nicht verändert.',
  },
  airkey: {
    label: 'AirKey Zutrittsverwaltung',
    description:
      'EVVA-AirKey-Verwaltung — Menüeintrag "AirKey", Routen /airkey und /airkey/mappings sowie die AirKey-Aktionen in Benutzerverwaltung und Mitarbeiterstatus.',
    stage: 'upcoming',
    since: '2026-08-03',
    owner: 'max@laserzone.de',
    pageRoutes: ['/airkey'],
    partialRoutes: ['/userManagement', '/employeeStatus'],
    note: 'Deaktiviert, weil das Feature im laufenden Betrieb Probleme macht. Code, Rechte und bereits erfasste AirKey-Daten bleiben erhalten. Das Backend blockt zusätzlich alle /api/v1/airkey/*-Endpoints, solange dort AIRKEY_ENABLED=true fehlt — beide Seiten müssen zum Reaktivieren gesetzt werden.',
  },
  pizzaTreatment: {
    label: 'Pizzaliste (Düsseldorf)',
    description:
      'Pizzaliste/Vorbestellung-Tab in der Küchen-Ansicht inkl. Anruf-Aufgaben (Kindergeburtstage ohne Pizza) und Lieferanten-Übergabe für Standorte mit isSpecialPizzaTreatment.',
    // Live since release 8.0 — the tab only appears for branches with
    // isSpecialPizzaTreatment anyway; the backend needs PIZZA_TREATMENT_ENABLED=true.
    stage: 'live',
    since: '2026-08-10',
    owner: 'max@laserzone.de',
    // Extra tab inside the kitchen view, which itself is live.
    pageRoutes: [],
    partialRoutes: ['/kitchen'],
    note: 'Das Backend gated die Pizza-Vorbestellungs-Endpunkte zusätzlich mit PIZZA_TREATMENT_ENABLED (503 PIZZA_TREATMENT_DISABLED); Staging an, Live aus — beide Seiten müssen zum Freischalten gesetzt sein.',
  },
  kitchenPreviewEdit: {
    label: 'Küche: Pizzen in der Tagesvorschau planen',
    description:
      'In „Heute in der Küche“ des führenden Dashboards (funzone-management-frontend-new, /kitchen) je Buchung „Planen“: Servierzeit setzen oder auf Standard zurück und die Sorte jeder All-Inc-Pizza wählen, auch vor dem Einchecken. Ohne Oberfläche in diesem Dashboard - es ist eingefroren.',
    stage: 'upcoming',
    since: '2026-10-08',
    owner: 'florian@laserzone.de',
    // No surface here: the day preview exists only in the leading dashboard.
    pageRoutes: [],
    partialRoutes: [],
    note: 'Hier nur registriert, damit die vier Stellen des Registers übereinstimmen. Reines Frontend-Flag im führenden Dashboard; nutzt den bestehenden PATCH /internalBooking/:id/pizzaService (Recht internalBooking:update:minor).',
  },
};

// camelCase flag key -> env var suffix, e.g. staffCostAreaOverview ->
// STAFF_COST_AREA_OVERVIEW.
const toEnvSuffix = key => key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();

// True while the bundle was built for the staging environment. Kept as a
// module-level constant on purpose: Vite inlines `import.meta.env` as an object
// literal, and directly after a concise arrow body (`() => import.meta.env…`)
// that literal would be parsed as a function block and break the build.
const IS_STAGING_BUILD = import.meta.env?.VITE_IS_STAGING === 'true';

/**
 * Whether this bundle was built for staging. Use it for tooling that must never
 * exist on live — a test run that really sends mail, a debug view. It is a
 * build-time constant, so such a control is not merely hidden on live but
 * absent from the bundle. The matching endpoints refuse outside staging on
 * their own as well; this only keeps the button from being offered.
 */
export const isStagingBuild = () => IS_STAGING_BUILD;

// Whether a gated feature is currently enabled. An env override always wins; a
// postponed feature stays off even on staging; staging shows every other
// registered flag; otherwise the registry stage decides. Unknown keys are
// treated as disabled everywhere — including staging — so a typo can never
// silently expose something.
export const isFeatureEnabled = key => {
  const flag = FEATURE_FLAGS[key];
  if (!flag) return false;
  const envValue = import.meta.env?.[`VITE_FEATURE_${toEnvSuffix(key)}`];
  if (envValue === 'true') return true;
  if (envValue === 'false') return false;
  // Checked before the staging rule on purpose: a postponed project has nothing
  // to review, so it comes off the test environment too. Only the explicit env
  // override above brings it back.
  if (flag.stage === 'postponed') return false;
  if (IS_STAGING_BUILD) return true;
  // 'secretLive' is on in production like 'live' — the two differ only in what may
  // be said about them, never in whether they run.
  return flag.stage === 'live' || flag.stage === 'secretLive';
};

// A feature's declared state. An unknown key resolves to 'secret' on purpose —
// the most restrictive answer, so a typo can never make something look
// announceable.
export const featureStage = key => FEATURE_FLAGS[key]?.stage ?? 'secret';

// Whether nothing about this feature may be shown here. True while the feature is
// secret, stagingOnly or postponed AND its flag is off — on staging a secret feature is
// ordinary, a postponed one stays hidden there as well. Use this wherever an off
// feature would otherwise still leave a trace (a teaser, a disabled menu entry,
// an explanatory note).
export const isFeatureSecret = key => {
  // Asked before the switch: a secretly live feature is on AND unmentionable, so it
  // stays hidden precisely while it works.
  if (featureStage(key) === 'secretLive') return true;
  if (isFeatureEnabled(key)) return false;
  const stage = featureStage(key);
  return stage === 'secret' || stage === 'postponed' || stage === 'stagingOnly';
};

// All flags that are currently visible although they would be off on live —
// i.e. everything the current environment shows on top of production. On live
// this is always empty, because no flag resolves to enabled while its stage is
// not 'live'. Each entry is the registry entry plus its `key`.
export const getActiveFeatureFlags = () =>
  Object.entries(FEATURE_FLAGS)
    // 'secretLive' runs everywhere, so it is not something this environment shows on
    // top of production — and naming it in the banner would announce it.
    .filter(
      ([key, flag]) => isFeatureEnabled(key) && flag.stage !== 'live' && flag.stage !== 'secretLive'
    )
    .map(([key, flag]) => ({ key, ...flag }));

const normalizePath = pathname => {
  const trimmed = String(pathname || '/').trim();
  if (trimmed.length > 1 && trimmed.endsWith('/')) return trimmed.slice(0, -1);
  return trimmed || '/';
};

// A route matches when it is the path itself or one of its parent segments, so
// '/airkey' also covers '/airkey/mappings'.
const routeMatches = (pathname, route) => {
  const path = normalizePath(pathname);
  const candidate = normalizePath(route);
  return path === candidate || path.startsWith(`${candidate}/`);
};

// Splits the active flags into the ones that make the given page exist at all
// and the ones that only add a part to an otherwise live page. Flags whose
// surface has no route (modal buttons, global listeners) appear in neither.
export const getFeatureFlagsForPath = pathname => {
  const active = getActiveFeatureFlags();
  const matches = (flag, field) =>
    Array.isArray(flag[field]) && flag[field].some(route => routeMatches(pathname, route));

  return {
    pageFlags: active.filter(flag => matches(flag, 'pageRoutes')),
    partialFlags: active.filter(
      flag => !matches(flag, 'pageRoutes') && matches(flag, 'partialRoutes')
    ),
  };
};
